/*
  kap_gallery-drag.js — click-and-drag scrolling for the product media gallery.

  The gallery is a native scroll-snap container (see kap_main-product.css). Those respond to
  touch and trackpad gestures but ignore a mouse click-drag entirely, which is why the desktop
  gallery felt frozen. This adds the missing behaviour and nothing else: arrows, snapping and
  thumbnail syncing all stay with Dawn's SliderComponent / media-gallery.

  Notes:
  - Mouse pointers only. Touch already scrolls natively, and hijacking it would fight the
    browser's own momentum.
  - A drag past DRAG_THRESHOLD swallows the click that follows, so dragging across the image
    doesn't also open the lightbox.
*/
(function () {
  const SELECTOR = '.product__media-wrapper .product__media-list';
  const DRAG_THRESHOLD = 5;

  function init(list) {
    if (list.dataset.kapDrag === 'ready') return;
    list.dataset.kapDrag = 'ready';

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let swallowClick = false;

    const scrollable = () => list.scrollWidth > list.clientWidth + 1;

    const markDraggable = () => list.classList.toggle('is-draggable', scrollable());
    markDraggable();
    new ResizeObserver(markDraggable).observe(list);

    list.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !scrollable()) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = list.scrollLeft;
      dragging = false;
    });

    list.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        list.classList.add('is-dragging');
        // Capture so the drag survives the pointer leaving the gallery mid-swipe.
        try { list.setPointerCapture(pointerId); } catch (e) {}
      }
      list.scrollLeft = startScroll - dx;
      event.preventDefault();
    });

    function endDrag(event) {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      try { list.releasePointerCapture(pointerId); } catch (e) {}
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      swallowClick = true;
      // Dropping .is-dragging restores scroll-snap, which settles on the nearest slide.
      list.classList.remove('is-dragging');
    }

    list.addEventListener('pointerup', endDrag);
    list.addEventListener('pointercancel', endDrag);

    list.addEventListener(
      'click',
      (event) => {
        if (!swallowClick) return;
        swallowClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Stop the browser's native image drag from pre-empting ours.
    list.addEventListener('dragstart', (event) => {
      if (event.target.closest(SELECTOR)) event.preventDefault();
    });
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // Variant changes and the theme editor re-render the gallery markup.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches && node.matches(SELECTOR)) init(node);
        else scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
